import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { API_URL } from "../utils/api";

// Identifiants des fournisseurs — à définir dans .env.local (non versionné), même convention
// que REACT_APP_API_URL dans utils/api.ts :
//   REACT_APP_GOOGLE_CLIENT_ID_WEB=xxxxxxxx.apps.googleusercontent.com  (client OAuth "Web application")
//   REACT_APP_APPLE_SERVICES_ID=com.oltodo.web                          (Services ID Apple, PAS le bundle id de l'app iOS)
//   REACT_APP_APPLE_REDIRECT_URI=https://oltodo.com/login               (optionnel — défaut : origine courante + /login ;
//                                                                        doit être déclaré à l'identique dans la console Apple)
// Tant que ces variables sont absentes (configuration Google Cloud / Apple Developer en cours),
// les boutons s'affichent désactivés : la page ne casse pas et rien ne prétend fonctionner.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID_WEB || "";
const APPLE_SERVICES_ID = process.env.REACT_APP_APPLE_SERVICES_ID || "";
const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI ||
  (typeof window !== "undefined" ? `${window.location.origin}/login` : "");

const GIS_SRC = "https://accounts.google.com/gsi/client";
const APPLE_SRC = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

interface AppleSignInResponse {
  authorization?: { id_token?: string; code?: string; state?: string };
  // Apple ne renvoie `user` que lors de la toute première autorisation accordée pour ce
  // couple compte Apple + app, jamais lors des connexions suivantes.
  user?: { name?: { firstName?: string; lastName?: string }; email?: string };
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (response: { credential?: string }) => void }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          usePopup: boolean;
          nonce?: string;
          state?: string;
        }) => void;
        signIn: () => Promise<AppleSignInResponse>;
      };
    };
  }
}

const AppleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const GoogleLogo = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Chargement paresseux et mutualisé des SDK : les deux pages (Login, Register) montent ce
// composant, on ne veut ni deux balises <script> ni deux téléchargements. Le rejet de la
// promesse (bloqueur de pub, réseau coupé) est ce qui permet de désactiver proprement le
// bouton concerné au lieu de laisser une zone cliquable morte.
const scriptPromises: Record<string, Promise<void>> = {};

const loadScript = (src: string): Promise<void> => {
  if (!scriptPromises[src]) {
    scriptPromises[src] = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        // On oublie la promesse rejetée pour qu'un remontage puisse réessayer le chargement.
        delete scriptPromises[src];
        script.remove();
        reject(new Error(`Impossible de charger ${src}`));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromises[src];
};

// Nonce aléatoire cryptographique en hexadécimal. Volontairement basé sur getRandomValues et
// non sur crypto.randomUUID(), absent des Safari < 15.4 que l'app supporte encore.
const randomNonce = (): string => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < bytes.length; i++) out += bytes[i].toString(16).padStart(2, "0");
  return out;
};

// Boutons icône-seule, côte à côte, parfaitement carrés et symétriques — plus de largeur pleine
// ni de texte "Continuer avec...". ICON_BUTTON_SIZE est la valeur de repli tant que le vrai
// bouton Google (rendu par leur propre SDK, seule source de vérité pour sa taille réelle) n'a
// pas encore été mesuré ; voir l'effect de mesure plus bas, qui aligne ensuite le bouton Apple
// (entièrement à nous) sur cette mesure au pixel près, dans les deux sens (largeur ET hauteur).
const ICON_BUTTON_SIZE = 48;
const providerButtonStyle: React.CSSProperties = {
  width: ICON_BUTTON_SIZE, height: ICON_BUTTON_SIZE, display: "flex", alignItems: "center", justifyContent: "center",
  border: "1.5px solid #E0E0E0", borderRadius: 14,
  backgroundColor: "white", boxSizing: "border-box", transition: "background 0.15s", padding: 0,
};

interface Props {
  /** Remonte le message d'erreur à la page, qui l'affiche dans sa bannière existante. */
  onError: (message: string) => void;
}

export default function SocialAuthButtons({ onError }: Props) {
  const { lang, setToken, setUser } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Un visiteur venu d'une fiche publique ("Revendiquer ce lieu") ou de /pro ("Créer ma page")
  // porte son intention dans ce paramètre — voir RedirectToLogin dans App.tsx.
  const redirect = searchParams.get("redirect");
  const [busy, setBusy] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [appleReady, setAppleReady] = useState(false);
  const googleHostRef = useRef<HTMLDivElement | null>(null);
  // Doublon en ref de `busy` : le callback de Google Identity Services est enregistré une seule
  // fois auprès du SDK, il ne verrait jamais la valeur d'état à jour.
  const busyRef = useRef(false);

  // Ouverture de session strictement identique à celle de la connexion par mot de passe
  // (mêmes clés localStorage via le contexte, même destination) — hormis le détour par le
  // profil quand il manque au compte une ville ou un téléphone vérifié.
  const finishAuth = useCallback((data: any) => {
    setToken(data.accessToken, data.refreshToken);
    setUser(data.user);
    navigate(data.needsProfileCompletion ? "/profile?tab=Infos" : (redirect || "/"));
  }, [navigate, setToken, setUser, redirect]);

  const postAuth = useCallback(async (path: string, body: Record<string, unknown>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    onError("");
    try {
      // fetch direct plutôt que api.post : on a besoin du code HTTP pour distinguer un jeton
      // invalide (401) d'un compte suspendu (403) ou d'un email déjà pris (409), et api.post
      // ne renvoie que le corps JSON. C'est aussi ce que font déjà login/register/forgot-password.
      const res = await fetch(`${API_URL}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        // 401 : le détail serveur ("jeton invalide ou expiré") n'aide en rien l'utilisateur.
        // 403 (compte suspendu) et 409 (email déjà utilisé par un compte mot de passe) portent
        // au contraire une consigne précise : on affiche le message du serveur tel quel.
        onError(res.status === 401 ? t(lang, "social_auth_failed") : data.error || t(lang, "social_auth_failed"));
      } else if (data.requires_2fa) {
        // Compte avec la double authentification activée (voir Settings > Sécurité) : plutôt que
        // de dupliquer l'écran de saisie du code dans chacun des 3 endroits où ce composant est
        // monté, on renvoie vers la connexion classique par mot de passe, seul endroit qui sait
        // déjà terminer ce parcours.
        onError(t(lang, "twofa_use_password_login"));
      } else {
        finishAuth(data);
      }
    } catch {
      onError(t(lang, "error_connection"));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, [finishAuth, onError]);

  const postAuthRef = useRef(postAuth);
  useEffect(() => { postAuthRef.current = postAuth; }, [postAuth]);

  // --- Google Identity Services ---
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      console.warn("[OAuth] REACT_APP_GOOGLE_CLIENT_ID_WEB non défini, bouton Google désactivé.");
      return;
    }
    let cancelled = false;
    loadScript(GIS_SRC)
      .then(() => {
        if (cancelled || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: response => {
            // Pas de credential = flux abandonné côté Google : rien à signaler à l'utilisateur.
            if (!response || !response.credential) return;
            postAuthRef.current("/auth/google", { idToken: response.credential });
          },
        });
        setGoogleReady(true);
      })
      .catch(() => {
        console.warn("[OAuth] Google Identity Services n'a pas pu être chargé, bouton Google désactivé.");
      });
    return () => { cancelled = true; };
  }, []);

  // Le vrai bouton Google (rendu par le SDK : exigence de marque, et seul moyen d'obtenir un id_token
  // sur un clic) est posé INVISIBLE par-dessus notre propre bouton, qui affiche toujours le logo.
  // Avant, c'était le SDK qui dessinait aussi le logo : si l'origine n'était pas autorisée côté
  // Google (ex. www.oltodo.com), le SDK vidait son bouton et il ne restait qu'un carré blanc.
  // Le bouton natif de Google fait 40px ; on l'agrandit à 48 (scale 1.2) pour que toute notre
  // surface visible soit cliquable.
  useEffect(() => {
    if (!googleReady) return;
    const host = googleHostRef.current;
    if (!host || !window.google) return;
    host.innerHTML = "";
    window.google.accounts.id.renderButton(host, {
      type: "icon",
      shape: "square",
      theme: "outline",
      size: "large",
      locale: lang,
    });
  }, [googleReady, lang]);

  // --- Sign in with Apple ---
  useEffect(() => {
    if (!APPLE_SERVICES_ID) {
      console.warn("[OAuth] REACT_APP_APPLE_SERVICES_ID non défini, bouton Apple désactivé.");
      return;
    }
    let cancelled = false;
    loadScript(APPLE_SRC)
      .then(() => { if (!cancelled && window.AppleID) setAppleReady(true); })
      .catch(() => {
        console.warn("[OAuth] Le SDK Sign in with Apple n'a pas pu être chargé, bouton Apple désactivé.");
      });
    return () => { cancelled = true; };
  }, []);

  const handleApple = async () => {
    if (!appleReady || !window.AppleID || busyRef.current) return;
    onError("");
    // Nonce brut conservé le temps de l'échange : le SDK Apple en envoie le SHA-256 à Apple,
    // qui le replace dans le jeton d'identité. Le serveur re-dérive ce hash depuis la valeur
    // brute transmise ici pour vérifier que le jeton correspond bien à CETTE tentative
    // (protection anti-rejeu). On ne hache donc jamais nous-mêmes avant l'envoi.
    const rawNonce = randomNonce();
    try {
      window.AppleID.auth.init({
        clientId: APPLE_SERVICES_ID,
        scope: "name email",
        redirectURI: APPLE_REDIRECT_URI,
        usePopup: true,
        nonce: rawNonce,
      });
      const response = await window.AppleID.auth.signIn();
      const identityToken = response && response.authorization ? response.authorization.id_token : undefined;
      if (!identityToken) {
        onError(t(lang, "social_auth_failed"));
        return;
      }
      // `user.name` n'est présent qu'à la toute première autorisation : c'est notre unique
      // occasion de le transmettre, il ne reviendra jamais (le serveur se charge de le stocker).
      await postAuth("/auth/apple", {
        identityToken,
        nonce: rawNonce,
        name: response.user ? response.user.name : undefined,
      });
    } catch (err: any) {
      // Popup fermée ou autorisation refusée : c'est un choix de l'utilisateur, pas une panne.
      const code = err && err.error;
      if (code === "popup_closed_by_user" || code === "user_cancelled_authorize" || code === "user_trigger_new_signin_flow") return;
      console.warn("[OAuth] Sign in with Apple a échoué :", err);
      onError(t(lang, "social_auth_failed"));
    }
  };

  const providersUnavailableHint = t(lang, "social_auth_unavailable");

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
        <div style={{ flex: 1, height: 1, backgroundColor: "#E0E0E0" }} />
        <span style={{ fontSize: 12, color: "#999" }}>{t(lang, "or_divider")}</span>
        <div style={{ flex: 1, height: 1, backgroundColor: "#E0E0E0" }} />
      </div>

      {/* Icônes seules, côte à côte, carrées et identiques (48px). Apple reste toujours affiché (grisé
          tant que le compte Apple Developer n'est pas configuré), à la demande du fondateur. */}
      <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 14, opacity: busy ? 0.6 : 1, pointerEvents: busy ? "none" : "auto" }}>
        {(
          <button
            type="button"
            onClick={handleApple}
            disabled={!appleReady}
            aria-label={t(lang, "continue_with_apple")}
            title={appleReady ? t(lang, "continue_with_apple") : providersUnavailableHint}
            style={{ ...providerButtonStyle, cursor: appleReady ? "pointer" : "not-allowed", opacity: appleReady ? 1 : 0.45 }}
            onMouseOver={e => { if (appleReady) e.currentTarget.style.backgroundColor = "#F9F9F9"; }}
            onMouseOut={e => { e.currentTarget.style.backgroundColor = "white"; }}
          >
            <AppleLogo />
          </button>
        )}

        {GOOGLE_CLIENT_ID && (
          <div
            style={{ ...providerButtonStyle, position: "relative", overflow: "hidden", cursor: googleReady ? "pointer" : "not-allowed", opacity: googleReady ? 1 : 0.45 }}
            title={googleReady ? t(lang, "continue_with_google") : providersUnavailableHint}
          >
            <GoogleLogo />
            {googleReady && (
              <div
                ref={googleHostRef}
                aria-label={t(lang, "continue_with_google")}
                style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", opacity: 0.01, transform: "scale(1.2)", transformOrigin: "center" }}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
